import { orderPath } from "../../shared/sellerPaths";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { PLAN_LABEL, planWith } from "../../shared/market";
import { CUSTOMER_SORTS, CUSTOMERS_PAGE_SIZE, LIST_SEGMENTS, type CustomerRow, type CustomerSort, type SegmentBase } from "../../shared/marketCustomers";
import { Pagination } from "../components/Pagination";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { ApiError } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi } from "./api";
import { useArs } from "./components";
import { ShowcaseOr } from "./Showcase";

/** A personal coupon's code: easy to read out, unlikely to repeat in the shop. */
const personalCode = () => `PARA${Array.from({ length: 6 }, () => "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"[Math.floor(Math.random() * 32)]).join("")}`;

/** Mi tienda › Clientes (VMAX): who bought, the groups, what else they buy and who's after the shop's cards. */
export function CustomersTab({ allowed }: { allowed: boolean }) {
  const { t } = useI18n();
  if (!allowed) {
    return (
      <ShowcaseOr kind="customers">
        <section className="plan-card" aria-label={t("mkt.tab.clientes")}>
          <p>{t("cus.teaser", { plan: PLAN_LABEL[planWith("customers")] })}</p>
        </section>
      </ShowcaseOr>
    );
  }
  return (
    <div className="stack customers-tab">
      <CustomerList />
      <Interests />
      <Opportunities />
    </div>
  );
}

function CustomerList() {
  const { t, locale } = useI18n();
  const ars = useArs();
  const [segment, setSegment] = useState<SegmentBase>("all");
  const [sort, setSort] = useState<CustomerSort>("last");
  const [page, setPage] = useState(1);
  const [giving, setGiving] = useState<CustomerRow | null>(null);
  const data = useAsync((signal) => marketApi.customers({ segment, sort, page }, signal), [segment, sort, page]);
  const date = (iso: string) => new Date(iso).toLocaleDateString(locale === "es" ? "es-AR" : "en-US");
  const v = data.data;
  return (
    <>
      {v && (
        <ul className="stats-cards">
          <li>
            <span className="muted small">{t("cus.customers")}</span>
            <strong>{v.summary.customers}</strong>
          </li>
          <li>
            <span className="muted small">{t("cus.repeat")}</span>
            <strong>{v.summary.repeat}</strong>
            {v.summary.repeatPct !== null && <span className="muted small">{t("cus.repeatNote", { pct: v.summary.repeatPct })}</span>}
          </li>
          <li>
            <span className="muted small">{t("cus.avgTicket")}</span>
            <strong>{v.summary.avgTicketArs === null ? "—" : ars(v.summary.avgTicketArs)}</strong>
          </li>
          <li>
            <span className="muted small">{t("cus.repeatRevenue")}</span>
            <strong>{v.summary.repeatRevenuePct === null ? "—" : `${v.summary.repeatRevenuePct}%`}</strong>
            <span className="muted small">{t("cus.repeatRevenueNote")}</span>
          </li>
          <li>
            <span className="muted small">{t("cus.followers")}</span>
            <strong>{v.summary.followers}</strong>
          </li>
        </ul>
      )}
      <p className="muted small">{t("cus.privacy")}</p>
      <div className="btn-row customers-filters">
        <label>
          {t("cus.segment")}{" "}
          <select
            value={segment}
            onChange={(e) => {
              setSegment(e.target.value as SegmentBase);
              setPage(1);
            }}
          >
            {LIST_SEGMENTS.map((s) => (
              <option key={s} value={s}>
                {t(`cus.seg.${s}` as MessageKey)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("cus.sort")}{" "}
          <select value={sort} onChange={(e) => setSort(e.target.value as CustomerSort)}>
            {CUSTOMER_SORTS.map((s) => (
              <option key={s} value={s}>
                {t(`cus.sort.${s}` as MessageKey)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {giving && <PersonalCoupon customer={giving} onClose={() => setGiving(null)} />}
      {data.error ? (
        <ErrorMessage onRetry={data.reload} />
      ) : !v ? (
        <Loading />
      ) : v.rows.length === 0 ? (
        <p className="muted">{t("cus.empty")}</p>
      ) : (
        <>
          <div className="imp-table-wrap">
            <table className="stats-table">
              <thead>
                <tr>
                  <th scope="col">{t("cus.colName")}</th>
                  <th scope="col" className="num">{t("cus.colOrders")}</th>
                  <th scope="col" className="num">{t("cus.colSpent")}</th>
                  <th scope="col">{t("cus.colFirst")}</th>
                  <th scope="col">{t("cus.colLast")}</th>
                  <th scope="col">
                    <span className="sr-only">{t("offers.action")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {v.rows.map((r) => (
                  <tr key={r.key}>
                    <td>
                      <strong>{r.name}</strong>
                      <span className="customer-chips">
                        {r.segments.map((s) => (
                          <span key={s} className={s === "vip" ? "pill pill-paid" : s === "inactive" ? "pill pill-unpaid" : "pill"}>
                            {t(`cus.chip.${s}` as MessageKey)}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="num">{r.orders}</td>
                    <td className="num">{ars(r.spentArs)}</td>
                    <td>{date(r.firstAt)}</td>
                    <td>{date(r.lastAt)}</td>
                    <td>
                      <Link to={orderPath(r.lastOrder, "seller")} className="btn btn-link btn-sm">
                        {t("cus.lastOrder")}
                      </Link>
                      <button type="button" className="btn btn-link btn-sm" onClick={() => setGiving(r)}>
                        {t("cus.giveCoupon")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {v.total > CUSTOMERS_PAGE_SIZE && <Pagination page={page} totalPages={Math.ceil(v.total / CUSTOMERS_PAGE_SIZE)} onPage={setPage} />}
        </>
      )}
    </>
  );
}

/** A coupon only this customer can use (they see it in Mis cupones and at the shop's checkout). */
function PersonalCoupon({ customer, onClose }: { customer: CustomerRow; onClose: () => void }) {
  const { t } = useI18n();
  const [kind, setKind] = useState<"percent" | "amount">("percent");
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const days = Number(f.get("days") || 30);
    const code = personalCode();
    setBusy(true);
    setError(null);
    try {
      await marketApi.createCoupon({ code, kind, value: Number(f.get("value")), scope: "user", issuedTo: customer.key, perUserMax: 1, endsAt: new Date(Date.now() + days * 86_400_000).toISOString() });
      setMade(code);
    } catch (err) {
      const c = err instanceof ApiError ? err.code : "";
      setError(c === "coupon_limit" || c === "plan_required" ? t(`cpn.err.${c}` as MessageKey) : c === "validation_error" ? t("cpn.err.validation") : t("error.generic"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="plan-card stack-sm" onSubmit={submit} aria-labelledby="pc-title">
      <h3 id="pc-title">{t("cus.couponFor", { name: customer.name })}</h3>
      {made ? (
        <p role="status">{t("cus.couponMade", { name: customer.name, code: made })}</p>
      ) : (
        <div className="btn-row customers-filters">
          <label>
            {t("cpn.kind")}{" "}
            <select value={kind} onChange={(e) => setKind(e.target.value as "percent" | "amount")}>
              <option value="percent">{t("cpn.percent")}</option>
              <option value="amount">{t("cpn.amount")}</option>
            </select>
          </label>
          <label>
            {t("cpn.value")} ({kind === "percent" ? "%" : "$"}) <input name="value" type="number" required min={1} max={kind === "percent" ? 50 : undefined} className="qty-input" />
          </label>
          <label>
            {t("cus.couponDays")} <input name="days" type="number" min={1} max={365} defaultValue={30} className="qty-input" />
          </label>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
            {t("cpn.create")}
          </button>
        </div>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}
      <div>
        <button type="button" className="btn btn-link btn-sm" onClick={onClose}>
          {t("nav.close")}
        </button>
      </div>
    </form>
  );
}

function Interests() {
  const { t } = useI18n();
  const data = useAsync((signal) => marketApi.customerInterests(signal), []);
  if (!data.data) return null;
  const d = data.data;
  const lists: { key: "sets" | "categories" | "pokemon"; label: MessageKey }[] = [
    { key: "sets", label: "cus.sets" },
    { key: "categories", label: "cus.categories" },
    { key: "pokemon", label: "cus.pokemon" },
  ];
  const empty = lists.every((l) => d[l.key].length === 0);
  return (
    <section aria-labelledby="cus-int">
      <h3 id="cus-int">{t("cus.interests")}</h3>
      <p className="muted small">{t("cus.interestsHint")}</p>
      {empty ? (
        <p className="muted">{t("cus.interestsEmpty")}</p>
      ) : (
        <div className="interest-lists">
          {lists.map(
            (l) =>
              d[l.key].length > 0 && (
                <div key={l.key}>
                  <strong className="small">{t(l.label)}</strong>
                  <ol className="stats-list">
                    {d[l.key].map((r) => (
                      <li key={r.name}>
                        {r.name} <span className="muted small">· {t("cus.buyers", { count: r.buyers })}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ),
          )}
        </div>
      )}
    </section>
  );
}

function Opportunities() {
  const { t } = useI18n();
  const data = useAsync((signal) => marketApi.customerOpportunities(signal), []);
  if (!data.data) return null;
  const d = data.data;
  return (
    <section aria-labelledby="cus-opp">
      <h3 id="cus-opp">{t("cus.opportunities")}</h3>
      {d.products.length === 0 ? (
        <p className="muted">{t("cus.opportunitiesEmpty")}</p>
      ) : (
        <>
          <p className="muted small">{d.people === null ? t("cus.opportunitiesHintFew") : t("cus.opportunitiesHint", { count: d.people })}</p>
          <ul className="stats-list">
            {d.products.map((p) => (
              <li key={p.listingId}>
                <Link to={`/mercado/${p.listingId}`}>{[p.name, p.setName, p.cardNumber && `#${p.cardNumber}`].filter(Boolean).join(" · ")}</Link>{" "}
                <span className="muted small">· {p.people === null ? t("cus.peopleFew") : t("cus.people", { count: p.people })}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
